import {Inject, Injectable} from "@angular/core";
import {Observable} from "rxjs";
import "rxjs/add/operator/map";
import "rxjs/add/operator/share";
import "rxjs/add/operator/subscribeOn";

import {MicrophoneService, MicrophoneStatus} from "../audio/microphone.service";
import {CssSentence, CssWord} from "../../content/content-view.objects";
import {RecognizerLauncherService} from "./recognizer-launcher.service";
import {RecognizerGrammarService} from "./recognizer-grammar.service";
import {IDictionaryEntry, IExportDictionary, Pronunciation} from "../../api/data-model";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {DebugCommand} from "../../event/DebugEvent";

import {EventBus} from "../../event/EventBus";
import {RaVersion} from "../configuration/ra.version";
import {share} from "rxjs/operators";
import {of} from "rxjs/internal/observable/of";
import {ErrorType, INTERVENTIONTYPE, Scilearn} from "../../constants";


// recognized item can be a word, silence, noise, etc.
export interface IRecognizedItem {
    word: string; // not really right to call this 'word', but don't want to mess with what is returned
                  // from the worker at this point
    start: number;
    end: number;
    pronNum?: number; // if there are multiple pronunciations for a word, identify which one was actually recognized
    isWord: boolean; // whether it is a true word or silence, filler, etc.

}
//
export interface IRecognizedSequence {
    data: IRecognizedItem[];
    // only used/true in mock testing
    endOfFile?: boolean;
    // used with debug keys
    debugCommand?:DebugCommand;
}

@Injectable()
export class RecognizerService {

    // Initialize pocketsphinx.js worker and audio input
    private worker;
    private callBackId:number;
    private callBackPool;

    private initComplete = false; // maybe can eliminate with observables?
    private globalDict = {}; // TBD: add typing here IExportDictionary
    public initializationObservable:Observable<string>;
    private initializationObserver: any;
    public resultObservable:Observable<IRecognizedSequence>;
    private resultObserver: any;
    private audioPlayback = null;
    private sentenceStart = null;
    private recognizerGrammarService;
    private recognizerLauncherService;
    private pronunciationCorrectness = 50;
    private processErrorCount = 0;

    constructor(private microphoneService: MicrophoneService,
                private eventBus: EventBus, private loggingService: BugsnagLoggingService,
                @Inject(String)private workerPath?: string) {
        // if (!window["cordova"]) {
        //    this.audioRecorder = this.microphoneService;
        // }
        this.recognizerGrammarService = new RecognizerGrammarService();

        if (workerPath) {
          this.recognizerLauncherService = new RecognizerLauncherService(workerPath);
        } else {
          this.recognizerLauncherService = new RecognizerLauncherService();
        }

        this.initCallBacks();
        this.initializationObservable = new Observable<string>(observer =>
            this.initializationObserver = observer).pipe(share());
        this.resultObservable = new Observable<IRecognizedSequence>(observer =>
            this.resultObserver = observer).pipe(share());

        this.initializationObservable.subscribe(data => {
            this.loggingService.info("recognition service: listening to itself: " + data);
        });
    }

    private initCallBacks(): void {
        this.callBackId = 0;
        this.callBackPool = {};
    }
    private addCallBack(callBack: Function): number {
        let id = this.callBackId;
        this.callBackPool[id] = callBack;
        this.callBackId++;
        return id;
    }

    private getCallBack(id): Function {
        if (this.callBackPool.hasOwnProperty(id)) {
            let callBack = this.callBackPool[id];
            delete this.callBackPool[id];
            return callBack;
        }
        return null;
    }

    // A convenience function to post a message to the worker and associate
    // a callback to its response
    private postWorkerJob(message, callBack): void {
        let msg = message || {callbackId: undefined};
        msg.callbackId = this.addCallBack(callBack);
        // uploadLogger.log("TRACE", "RAWEB",
        // "worker posting message to worker per successful initialization from command: " + msg.command);
        // uploadLogger.log("TRACE", "RAWEB", "workerContainer posting msg to worker: " + JSON.stringify(msg));

        if (this.worker) {
            this.worker.postMessage(msg);
            this.loggingService.debug("Posted worker msg: " + JSON.stringify(msg));
        } else {
            this.loggingService.error("Recognizer worker does not exist", ErrorType.RECOGNIZER);
        }
    }

    // This function initializes an instance of the audioRecorder
    // it posts a message right away and calls onReady when it
    // is ready so that onmessage can be properly set
    private spawnWorker(): void {
        this.loggingService.log("calling spawnWorker");
        this.worker = this.recognizerLauncherService.getWorker();
        this.loggingService.log("finished creating worker.");

        this.worker.onmessage = (event) => {

            if (event.data.hasOwnProperty("statusMessage") && event.data.statusMessage === "importScripts finished") {
                this.loggingService.info("Worker loaded, ready to initialize");
                // Once the worker is fully loaded (has loaded the pocketsphinx.js and data,
                // we can call the initialize function
                this.initRecognizer();
            }

            if (event.data.hasOwnProperty("id")) {
                let callBack = this.getCallBack(event.data.id);
                let data = {};
                if (event.data.hasOwnProperty("data")) {
                    data = event.data.data;
                }
                if (callBack) {
                    callBack(data);
                }
            }
            // This is a case when the recognizer has a new hypothesis
            if (event.data.hasOwnProperty("hyp")) {

                let msg = "";
                if (event.data.hasOwnProperty("isFinal") && event.data.isFinal) {
                    msg = "Final ";
                }
                // this.loggingService.debug(msg + "hypdetail:" + JSON.stringify(event.data.hypDetail));
                // final recognitions can't be used because they are the result of a decision (e.g. to switch sentences)
                if (!(event.data.hasOwnProperty("isFinal") && event.data.isFinal)) {
                    // convert to observable
                    // this.plugoutService.newRecognitionResult(event.data.hypDetail);
                    if (this.resultObserver && !this.isMute) {
                        let result:IRecognizedSequence = {data: event.data.hypDetail, endOfFile: event.data.endOfFile};
                        if (this.resultObserver.isUnsubscribed) {
                            this.loggingService.error("resultObserver isUnsubscribed!");
                        }
                        this.resultObserver.next(result);
                    }
                } else {
                    this.loggingService.debug(msg + "hypdetail:" + JSON.stringify(event.data.hypDetail));
                }
            }

            // This is the case when we have an error
            if (event.data.hasOwnProperty("statusMessage") && (event.data.statusMessage === "error")) {
                if (event.data.code === "recognizer init not complete - click Try Again") {
                    setTimeout(() => {this.initRecognizer(); }, 1000);
                } else {
                    if (event.data.command === "process") {
                        // log this only once per session, otherwise we are spamming bugsnag since we call process many
                        // times a second when recognizing
                        this.processErrorCount++;
                        if (this.processErrorCount === Scilearn.RECOGNITION_ERROR_THRESHOLD) {
                            this.loggingService.error("Errors exceeded threshold for recognition process command");
                        }
                    } else {
                        this.loggingService.error("Error in " + event.data.command + " with error code "
                            + JSON.stringify(event.data.code));
                    }
                }
            }
        };
        this.worker.postMessage(this.getRecognitionPath());
        this.loggingService.info("exiting spawnWorker");
    }

    private getRecognitionPath():string {
        // tslint:disable-next-line:no-string-literal
        if (window["recognitioniOSWorker"]) {
            this.loggingService.info("posting script load message for native recognizer");
            return "rav2-init-ios";
        } else {
            this.loggingService.info("posting script load message with " + this.getPocketSphinxPath());
            return this.getPocketSphinxPath();
        }
    }

    private getPocketSphinxPath():string {
        return "pocketsphinx.js" + RaVersion.pocketsphinxVersion;
    }

    // This initializes the worker. When it calls back, we add words
    private initRecognizer(): void {
        let params = [["-remove_noise", "no"], ["-remove_silence", "no"], ["-lw", "10.0"],
            ["-fwdflat", "no"], ["-pl_window", "0"], ["-maxhmmpf", "100"],
            ["-cmninit", "9.6,-0.5,0,-0.2,-0.4,0,-0.1,-0.1,-0.1,-0.2,0,0.1,0"]];
        params = params.concat(this.getDynamicRecognitionParameters());
        console.log("calling init with params: " + JSON.stringify(params));
        this.postWorkerJob({
                command: "initialize",
                data: params
            },
            () => {
                this.microphoneService.setConsumers([this.worker]);
                this.postWorkerJob({command: "suppressLogs"}, null);
                this.loggingService.info("recognition worker init done");
                this.initComplete = true;
                this.initializationObserver.next("recognizer initialization complete");
            }
        );
    }

    private getDynamicRecognitionParameters(): Array<Array<string>> {
        let scale:number = this.pronunciationCorrectness / 100.0;
        let min = 0.4;
        let max = 2.0;
        // scale linearly from min to max as pronunciation correctness goes from 0 to 100
        let wordInsertionPenalty = min + scale * (max - min);
        console.log("pronunciationCorrectness: " + this.pronunciationCorrectness + " scale:" + scale.toString() +
                    " min: " + min.toString() + " max: " + max.toString() + " wip:" + wordInsertionPenalty.toString());
        // only one parameter is scaled in current implementation
        return [["-wip", wordInsertionPenalty.toString()]];
    }

    get recognizerResultObservable(): Observable<IRecognizedSequence> {
        return this.resultObservable;
    }

    get isInitialized(): boolean {
        return this.initComplete;
    }

    // This starts recording and recognizing with a grammar built for sentence
    public start(sentence:CssSentence, pronCorrectness:number, volumeCallback:Function): void {

        if (volumeCallback) {
            this.microphoneService.setVolumeListener(volumeCallback);
        }
        let adjustedPronCorrectness =
            this.recognizerGrammarService.adjustPronunciationCorrectness(pronCorrectness, sentence.wcpmGoal());

        let grammar = this.recognizerGrammarService.create(sentence.words,
                                                           Object.keys(this.globalDict),
                                                           adjustedPronCorrectness,
                                                           INTERVENTIONTYPE.no_intervention);
        this.sentenceStart = new Date();
        this.loggingService.info("Recognition: _TEST_AUTOMATION_ starting sentence id " + sentence.id);
        this.postWorkerJob({ command: "startFrom", data: grammar }, () => {

        });
        this.microphoneService.start();

    }

    // Stops recording and recognizing
    public stop(options): void {
        this.loggingService.info("calling recognizer stop");
        this.microphoneService.setVolumeListener(null);
        this.microphoneService.stop(options);
    }

    public mute(): void {
        this.microphoneService.mute();
    }

    public unmute(): void {
        this.microphoneService.unmute();

    }
    public get isMute(): boolean {
        return this.microphoneService.isMute;
    }

    public reset(callBack): void {
        // reset recognition grammars
        this.postWorkerJob({ command: "resetGrammars" }, callBack);
    }

    public restartFrom(wordId, sentence, isAudioIntervention, pronCorrectness): void {
        let interventionType = isAudioIntervention ? INTERVENTIONTYPE.audio_intervention :
                               INTERVENTIONTYPE.visual_intervention;
        let wordIndex = sentence.words.findIndex((word) => { return word.id === wordId; });
        let words:CssWord[] = sentence.words.slice(wordIndex);
        let newGrammar = this.recognizerGrammarService.create(words, Object.keys(this.globalDict),
                                                              pronCorrectness, interventionType);
        this.postWorkerJob({ command: "restartFrom", data: newGrammar }, () => {
            let now = new Date();
            let interventionMSec = now.getTime() - this.sentenceStart.getTime();
            this.loggingService.info("Recognition: _TEST_AUTOMATION_ intervention at msec=" + interventionMSec);
        });
    }

    public isRecording(): boolean {
        return (this.microphoneService.isRecording());
    }

    // download audio - request
    public requestRecording(callback:Function, mp3DataFormat:string): void {
        this.loggingService.info("Requesting data");
        this.microphoneService.requestMP3(callback, mp3DataFormat);
    }

    // download audio - callback
    public downloadRecording(blob, name): void {
        // create URL and download recording
        // change to number files and send filename to log.
        let date = new Date();
        let str = date.getFullYear() + "-" + (date.getMonth() + 1) + "-"
                  + date.getDate() + "_" + date.getHours() + "_" + date.getMinutes() + "_" +
                  date.getSeconds() + "_" + date.getMilliseconds();
        let outputname = name + str + blob.type.replace("audio/", ".");
        // tslint:disable-next-line:no-string-literal
        let url = (window.URL || window["webkitURL"]).createObjectURL(blob);
        let link = window.document.createElement("a");
        link.href = url;

        link.download = outputname;
        link.click();
    }

    public playRecording = (blob, name) => {
        // create URL and download recording
        // tslint:disable-next-line:no-string-literal
        let url = (window.URL || window["webkitURL"]).createObjectURL(blob);
        this.audioPlayback = new Audio(url);
        this.audioPlayback.play();
    }

    // add the dictionary data
    public addDictionary(pronunciationDictionary:IExportDictionary):Observable<boolean> {
        // only add word if it has not already been added.  Would really be better to do this in pocketsphinx but
        // no access to the needed info currently
        let newDict = [];
        Object.keys(pronunciationDictionary).forEach((word) => {
            if (!this.globalDict[word]) {
                let pronunciations = pronunciationDictionary[word];
                if (pronunciations && pronunciations.length) {
                    newDict = newDict.concat(this.getSphinxEntries(word, pronunciations));
                    let foilEntry = this.getFoils(word, pronunciations);
                    if (foilEntry.pronunciations.length > 0) {
                        newDict = newDict.concat(this.getSphinxEntries(foilEntry.word, foilEntry.pronunciations));
                    }
                    let startEntry = this.getStart(word, pronunciations);
                    if (startEntry.pronunciations.length > 0) {
                        newDict = newDict.concat(this.getSphinxEntries(startEntry.word, startEntry.pronunciations));
                    }
                } else {
                    this.loggingService.error("ERROR! missing pronunciation for word: " + word, ErrorType.RECOGNIZER);
                }
            }
        });

        return new Observable<boolean>(observer => {
            this.loggingService.debug("pocketsphinx format dictionary:" + JSON.stringify(newDict));
            if (newDict.length > 0) {
                this.reset(() => {
                    this.loggingService.info("completed grammar reset");
                });
                this.init(this.pronunciationCorrectness).subscribe(() => {
                    this.postWorkerJob({command: "addWords", data: newDict}, () => {
                        this.loggingService.info("Completed addWords job.");
                        observer.next(true);
                    });
                });
            } else {
                observer.next(true);
            }
        });
    }

    private getSphinxEntries(word: string, pronunciations: Pronunciation[]): Array<Array <string>> {
        let newEntries: Array<Array <string>> = [];
        this.globalDict[word] = pronunciations;
        for (let i = 0; i < pronunciations.length; i++) {
            let j = i + 1;
            let wordString = word;
            if (j > 1) {
                wordString += "(" + j.toString() + ")";
            }
            this.loggingService.debug("_TEST_AUTOMATION_ adding pronunciation:"
                                    + wordString + ":" + pronunciations[i]);
            newEntries.push([wordString, pronunciations[i]]);
        }
        return newEntries;
    }

    private getFoils(word:string, pronunciations:Pronunciation[]): IDictionaryEntry {
        // foils are models of partial pronunciations of a word.  each "Pronunciation" is a string of phonemes
        // separated by spaces, e.g. ""EH L AH F AH N T".
        // Foil pronunciations are based on the shortest pronunciation length for a word.
        let sortedPronunciations = pronunciations.sort((s1, s2) => {
            return (s1.split(" ").length - s2.split(" ").length);
        });
        let foilEntry:IDictionaryEntry = {word: word + RecognizerGrammarService.FOIL_TAG, pronunciations: []};
        let basePronunciationList = sortedPronunciations[0].split(" ");
        // TODO: make the numbers below constants, or calculated based on final strategy
        let minFoilLength = 1;
        let deltaToEnd = 2;
        let maxFoilLength = basePronunciationList.length - deltaToEnd;
        for (let len = minFoilLength; len <= maxFoilLength; len++) {
            foilEntry.pronunciations.push(basePronunciationList.slice(0, len).join(" "));
        }
        return foilEntry;
    }

    private getStart(word:string, pronunciations:Pronunciation[]): IDictionaryEntry {
        // foils are models of partial pronunciations of a word.  each "Pronunciation" is a string of phonemes
        // separated by spaces, e.g. ""EH L AH F AH N T".
        // Foil pronunciations are based on the shortest pronunciation length for a word.
        let sortedPronunciations = pronunciations.sort((s1, s2) => {
            return (s1.split(" ").length - s2.split(" ").length);
        });
        let startEntry:IDictionaryEntry = {word: word + RecognizerGrammarService.START_TAG, pronunciations: []};
        let basePronunciationList = sortedPronunciations[0].split(" ");
        // TODO: make the numbers below constants
        let minFoilLength = 1;
        let maxFoilLength = basePronunciationList.length > 2 ? 1 : 0;
        for (let len = minFoilLength; len <= maxFoilLength; len++) {
            startEntry.pronunciations.push(basePronunciationList.slice(0, len).join(" "));
            // longer words get the initial phoneme followed by a reduced vowel as well
            // TODO: might want to do something else for words starting with a vowel
            if (basePronunciationList.length > 3) {
                startEntry.pronunciations.push(basePronunciationList[0] + " IH");
                startEntry.pronunciations.push(basePronunciationList[0] + " AH");
                startEntry.pronunciations.push(basePronunciationList[0] + " UH");
            }
        }
        return startEntry;
    }

    get isMock(): boolean {
        return this.microphoneService.isMock;
    }

    get recognitionBuffersPerSecond(): number {
        return this.microphoneService.getBuffersPerSec();
    }

    // When the page is loaded, we spawn a new worker worker and call getUserMedia to
    // request access to the microphone
    public init(pronunciationCorrectness: number):Observable<string> {
        if (this.initComplete) {
            this.loggingService.info("init already completed, returning");
            return of("init complete");
        }
        this.pronunciationCorrectness = pronunciationCorrectness;
        this.loggingService.info("pronunciation correctness set: " + this.pronunciationCorrectness.toString());
        this.loggingService.info(
            "Initializing web audio and speech worker, waiting for approval to access the microphone");
        this.spawnWorker();
        let type = this.isMock ? "mock" : "live";
        this.loggingService.info("calling request etc on microphone service of type " + type);
        return this.initializationObservable;
    }

    public requestMicrophone(): boolean {
        let alreadyInitialized = true;
        if (this.microphoneService.currentMicrophoneStatus === MicrophoneStatus.NOT_INITIALIZED) {
            this.microphoneService.request();
            alreadyInitialized = false;
        }
        return alreadyInitialized;
    }

    get microphoneStatusObservable(): Observable<MicrophoneStatus> {
        return this.microphoneService.microphoneStatus;
    }

    public audioDevices(): Object[] {
        return this.microphoneService.getAudioDevices();
    }

    public currentAudioDevice(): Object {
        return this.microphoneService.currentAudioDevice();
    }

    public requestPlayback(): void {
        this.microphoneService.requestPlayback(this.playRecording);
    }

    public stopPlayback(): void {
        if (this.audioPlayback) {
            this.audioPlayback.pause();
            this.audioPlayback.currentTime = 0;
        }
    }

    public startContinuousMicCheck(): void {
        this.microphoneService.startPostCheckWatch();
    }

    public annoyingGesture():void {
        if (this.microphoneService.isSafari()) {
            this.microphoneService.annoyingGesture();
        }
    }

}
